/* ---------------------------------------------------------------------
   <cart-reward-meter> - two-stage cart progress.

   Stage 1: free shipping at settings.cart_free_shipping_threshold.
   Stage 2: the DECALZ100 flat discount at its own minimum.

   Why this is not theme.js's <free-shipping-bar>: that element knows a
   single threshold, writes its message into the first <span> it finds,
   and drives a <progress-bar>. Two goals do not fit that contract, so
   this is a separate element with a separate tag name. theme.js is not
   touched, and its FreeShippingBar class is left registered and unused.

   The cart total is taken from the same "cart:change" event theme.js
   dispatches, using the same maths: shippable line items only, minus
   cart-level discounts. All amounts are in the shop's minor unit (paise).
   --------------------------------------------------------------------- */

const CartRewardMeter = class extends HTMLElement {
  connectedCallback() {
    this._onCartChanged = this._onCartChanged.bind(this);

    this.t1 = Math.round(parseFloat(this.getAttribute('threshold-1')) * (Shopify.currency.rate || 1));
    this.t2 = Math.round(parseFloat(this.getAttribute('threshold-2')) * (Shopify.currency.rate || 1));

    // Whole rupees. The store prices in whole units, and "₹299.00" in a
    // 0.6rem chip reads as noise.
    this._money = new Intl.NumberFormat(Shopify.locale, {
      style: 'currency',
      currency: Shopify.currency.active,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    this._appliedCodes = (this.dataset.appliedCodes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);

    this._copyEl = this.querySelector('[data-crm-copy]');
    if (this._copyEl) {
      this._onCopy = this._onCopy.bind(this);
      this._copyEl.addEventListener('click', this._onCopy);
    }

    document.addEventListener('cart:change', this._onCartChanged);
    this._render(parseFloat(this.getAttribute('total-price')) || 0);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this._onCartChanged);
    if (this._copyEl) this._copyEl.removeEventListener('click', this._onCopy);
    clearTimeout(this._copyTimer);
  }

  async _onCopy() {
    const code = this._copyEl.dataset.code;

    // navigator.clipboard needs a secure context; the storefront is https, but a
    // rejected permission or an older in-app browser still has to leave the
    // shopper with the code, so fall back to a selection-based copy.
    let ok = true;
    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      ok = this._legacyCopy(code);
    }
    if (!ok) return;

    const note = this.querySelector('[data-crm-copy-note]');
    this._copyEl.classList.add('is-copied');
    if (note) note.textContent = this.dataset.copyNoteDone;

    clearTimeout(this._copyTimer);
    this._copyTimer = setTimeout(() => {
      this._copyEl.classList.remove('is-copied');
      if (note) note.textContent = this.dataset.copyNote;
    }, 2000);
  }

  _legacyCopy(code) {
    const field = document.createElement('textarea');
    field.value = code;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:-9999px;opacity:0';
    document.body.appendChild(field);
    field.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }
    document.body.removeChild(field);
    return ok;
  }

  _onCartChanged(event) {
    const cart = event.detail['cart'];

    // Progress is measured on the subtotal BEFORE cart-level discounts.
    // Subtracting them (as the old single-goal bar did) breaks the moment the
    // reward applies: a ₹850 cart minus its own ₹100 reward reads as ₹750, the
    // meter decides the goal is no longer met, and the shopper watches a reward
    // they just earned disappear. Shopify judges the discount's own minimum on
    // the pre-discount subtotal too, so this matches checkout.
    const items = cart['items']
      .filter((item) => item['requires_shipping'])
      .reduce((sum, item) => sum + item['final_line_price'], 0);

    this._appliedCodes = (cart['cart_level_discount_applications'] || [])
      .filter((allocation) => allocation['type'] === 'discount_code')
      .map((allocation) => (allocation['title'] || '').toUpperCase());

    this._render(items);
  }

  _render(total) {
    const won1 = total >= this.t1;
    const won2 = total >= this.t2;
    const stage = won2 ? 'done' : (won1 ? '2' : '1');

    this.setAttribute('data-stage', stage);
    this.setAttribute('aria-valuenow', String(Math.min(total, this.t2)));

    // ---- headline: only ever the remaining chase, never the win ----
    const msg = this.querySelector('[data-crm-msg]');
    const eyebrow = this.querySelector('[data-crm-eyebrow]');
    const chip = (amount) =>
      `<span class="cart-reward-meter__amt">${this._money.format(amount / 100).replace(/\$/g, '$$$$')}</span>`;

    if (!won1) {
      eyebrow.textContent = this.dataset.eyebrow1;
      msg.innerHTML = this.dataset.msg1.replace('%amount%', chip(this.t1 - total));
    } else if (!won2) {
      eyebrow.textContent = this.dataset.eyebrow2;
      msg.innerHTML = this.dataset.msg2.replace('%amount%', chip(this.t2 - total));
    } else {
      eyebrow.textContent = this.dataset.eyebrowDone;
      msg.innerHTML = this.dataset.msgDone;
    }

    // ---- checklist rows ----
    this._row(this.querySelector('[data-crm-row="1"]'), won1, false);
    this._row(this.querySelector('[data-crm-row="2"]'), won2, true);

    // ---- two-colour track ----
    const pct = (value) => `${Math.max(0, Math.min(100, value))}%`;
    this.querySelector('[data-crm-fill="1"]').style.width = pct(Math.min(total, this.t1) / this.t2 * 100);

    const fill2 = this.querySelector('[data-crm-fill="2"]');
    fill2.style.width = won1 ? pct((Math.min(total, this.t2) - this.t1) / this.t2 * 100) : '0%';

    // ---- milestone flags ----
    this.querySelector('[data-crm-flag="1"]').classList.toggle('is-on', won1);
    this.querySelector('[data-crm-flag="2"]').classList.toggle('is-on', won2);

    // ---- the discount code line, hidden until stage 2 is in play ----
    const code = this.querySelector('[data-crm-code]');
    if (code) {
      code.hidden = !won1;
      code.textContent = won2 ? this.dataset.codeDone : this.dataset.codeNext;
    }

    // Leaving the done state (the shopper removed an item) must not strand the
    // button in its "copied" confirmation.
    if (this._copyEl && !won2) {
      clearTimeout(this._copyTimer);
      this._copyEl.classList.remove('is-copied');
      const note = this.querySelector('[data-crm-copy-note]');
      if (note) note.textContent = this.dataset.copyNote;
    }

    if (won2) this._maybeAutoApply();
  }

  /* ----------------------------------------------------------------------
     Put the earned discount on the cart instead of asking the shopper to
     remember a code. A discount link applies the code to their session; the
     theme then renders it as a discount pill above the subtotal.
     ---------------------------------------------------------------------- */
  async _maybeAutoApply() {
    const code = (this.dataset.rewardCode || '').toUpperCase();
    if (!code || this._applying) return;

    const applied = this._appliedCodes || [];

    // Already on the cart - nothing to do, just say so.
    if (applied.includes(code)) {
      this._markApplied();
      return;
    }

    // NEVER replace a code the shopper already has. This reward "can't combine
    // with other discounts", so applying it would silently throw away whatever
    // they were using - and the other offer (buy 2 get 1) is worth far more
    // than ₹100 on most carts.
    if (applied.length > 0) return;

    // If they deliberately removed it, leave it removed.
    if (sessionStorage.getItem('crm-declined-' + code)) return;
    if (this._autoApplied) return;

    this._applying = true;
    try {
      await fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart', { credentials: 'same-origin' });
      this._autoApplied = true;

      // The totals around us were rendered by Liquid before the discount
      // existed. On the cart page a reload is the honest fix - it redraws the
      // subtotal and the discount pill together. In the drawer a reload would
      // slam it shut, so there the meter states the outcome itself.
      if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
        window.location.reload();
        return;
      }
      this._markApplied();
    } catch (error) {
      // Applying failed - the code is still shown and copyable, so the shopper
      // is never left without a way to claim it.
    } finally {
      this._applying = false;
    }
  }

  _markApplied() {
    this.setAttribute('data-applied', 'true');
    const code = this.querySelector('[data-crm-code]');
    if (code) code.textContent = this.dataset.codeApplied || this.dataset.codeDone;
  }

  _row(row, won, isStage2) {
    if (!row) return;
    row.classList.toggle('cart-reward-meter__row--todo', !won);
    row.classList.toggle('cart-reward-meter__row--won', won && !isStage2);
    row.classList.toggle('cart-reward-meter__row--won-2', won && isStage2);
    row.querySelector('[data-crm-box]').textContent = won ? '✓' : '';
    row.querySelector('[data-crm-k]').textContent = won ? row.dataset.wonLabel : row.dataset.todoLabel;
  }
};

if (!window.customElements.get('cart-reward-meter')) {
  window.customElements.define('cart-reward-meter', CartRewardMeter);
}
